import { Injectable, computed, inject, signal } from '@angular/core';

import {
  BOOTSTRAP_API_PATH,
  BOOTSTRAP_TIMEOUT_MS,
  DEFAULT_BOOTSTRAP,
  DEFAULT_FAVICON,
  DEFAULT_LOGO
} from '@constants/bootstrap.constants';
import { environment } from '@environment/environment';
import { BootstrapResponse, TenantBootstrap } from '@models/bootstrap.model';
import { TenantThemeService } from '@services/tenant-theme.service';

/**
 * Loads the public, pre-authentication tenant bootstrap config and exposes it as
 * signals for the auth screens (logo, login copy, legal/footer, support, feature
 * flags).
 *
 * Runs once via `provideAppInitializer`, so it completes before the app renders
 * and the first paint is already branded — no cache needed for the initial load.
 *
 * Uses native `fetch` (like {@link ConfigService}) so the token/error/cache HTTP
 * interceptors never fire on this anonymous, public call — a failed bootstrap
 * must not surface an error toast or trigger auth redirects.
 *
 * Fail-open: any failure (network, timeout, non-2xx, bad body) leaves the
 * platform defaults in place; login always works.
 */
@Injectable({ providedIn: 'root' })
export class BootstrapService {
  private readonly tenantTheme = inject(TenantThemeService);

  /** Raw bootstrap config; starts at platform defaults. */
  private readonly config = signal<TenantBootstrap>(DEFAULT_BOOTSTRAP);

  // ── Branding ──────────────────────────────────────────────────────────────
  readonly logo = computed(() => this.config().branding.logo ?? DEFAULT_LOGO);
  readonly favicon = computed(
    () => this.config().branding.favicon ?? DEFAULT_FAVICON
  );

  // ── Login copy ──────────────────────────────────────────────────────────────
  readonly loginTitle = computed(() => this.config().login.title);
  readonly loginSubtitle = computed(() => this.config().login.subtitle);

  /** Tab / document title — tenant display name, else the platform copyright brand. */
  readonly displayName = computed(
    () => this.config().tenant?.displayName ?? 'EveryCRED'
  );

  // ── Legal / footer ───────────────────────────────────────────────────────────
  readonly footerText = computed(() => this.config().legal.footerText);
  readonly copyright = computed(
    () => this.config().legal.copyright ?? DEFAULT_BOOTSTRAP.legal.copyright
  );
  readonly privacyUrl = computed(
    () =>
      this.config().legal.privacyPolicyUrl ??
      DEFAULT_BOOTSTRAP.legal.privacyPolicyUrl
  );
  readonly termsUrl = computed(
    () => this.config().legal.termsUrl ?? DEFAULT_BOOTSTRAP.legal.termsUrl
  );

  // ── Support ──────────────────────────────────────────────────────────────────
  readonly supportEmail = computed(
    () => this.config().support.email ?? DEFAULT_BOOTSTRAP.support.email
  );
  readonly supportHelpUrl = computed(
    () => this.config().support.helpUrl ?? DEFAULT_BOOTSTRAP.support.helpUrl
  );

  // ── Feature flags ─────────────────────────────────────────────────────────────
  readonly selfRegistration = computed(
    () => this.config().features.selfRegistration
  );

  /**
   * Fetches the bootstrap config and applies it. Never rejects — on any failure
   * the defaults already applied remain in place.
   */
  async load(): Promise<void> {
    try {
      const fresh = await this.fetchBootstrap();
      if (fresh) {
        this.apply(fresh);
      }
    } catch {
      // Fail-open: keep platform defaults. (fetch errors, timeout, JSON parse.)
    }
  }

  /**
   * GETs `/public/bootstrap` with a hard timeout. Returns the `data` payload, or
   * `null` on any non-success so the caller keeps the defaults.
   */
  private async fetchBootstrap(): Promise<TenantBootstrap | null> {
    const url = `${environment.apiUrl}${BOOTSTRAP_API_PATH}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), BOOTSTRAP_TIMEOUT_MS);

    try {
      const res = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) {
        return null;
      }
      const body = (await res.json()) as BootstrapResponse;
      return body?.data ?? null;
    } finally {
      clearTimeout(timer);
    }
  }

  /** Stores the config and applies side effects (favicon, theme hook). */
  private apply(config: TenantBootstrap): void {
    this.config.set(config);
    this.setFavicon(this.favicon());
    // The tab title is owned by PageTitleStrategy, which reads displayName()
    // and combines it with the route title — see page-title-strategy.service.ts.
    // Future hooks (no-op today): runtime theming. i18n wiring is TODO.
    this.tenantTheme.applyTheme(config.theme);
  }

  /** Points the document favicon at `href`, creating the link tag if absent. */
  private setFavicon(href: string): void {
    let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  }
}
